import siteConfig from "@/variables";
import React from "react";

declare global {
  interface Window {
    xsso: {
      onEvent: CallableFunction;
      state: CallableFunction;
      id: CallableFunction;
    };
  }
}

export type XssoId = {
  acct: { game_name: string; tag_line: string };
  acr: string;
  country: string;
  email: string;
  locale: string;
  login_country: string;
  player_locale?: string;
  sub: string;
};

export type AuthState = {
  loading: boolean;
  isLoggedIn: boolean;
  login: CallableFunction;
  logout: CallableFunction;
  hookLoginFunctions: CallableFunction;
  id?: XssoId;
};

const EMPTY_AUTH_STATE: AuthState = {
  loading: true,
  isLoggedIn: false,
  login: () => void 0,
  logout: () => void 0,
  hookLoginFunctions: () => void 0,
};

const XSSOContext = React.createContext<AuthState>(EMPTY_AUTH_STATE);

export const useXSSOAuth = () => React.useContext(XSSOContext);

/**
 *
 * xxsoAuth is a helper to deal with setting and retriving the idToken from xsso-login.
 *
 * The hook tries to retrieve any existing token from cookies / storage,
 *
 *  */
export const XSSOProvider = ({ children }: { children?: React.ReactNode }) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(false);
  const [id, setId] = React.useState<XssoId>();
  const [authState, setAuthState] = React.useState<AuthState>(EMPTY_AUTH_STATE);
  const xssoHostURL = siteConfig.XSSO_HOST;

  const handleStateChange = (state: string) => {
    if (state === "init") {
      // xsso loading
      return;
    }
    if (state === "login_required" || state == "poll_aborted") {
      setIsLoggedIn(false);
    } else {
      // implicit XSSOState.Set || XSSOState.Unchanged || XSSOState.Limited - could be explicit here
      setIsLoggedIn(true);
      setId(window.xsso.id());
    }
    setLoading(false);
  };

  React.useEffect(() => {
    const observer = new MutationObserver((_, obse) => {
      if (!!window.xsso) {
        handleStateChange(window.xsso.state());
        window.xsso.onEvent(() => {
          handleStateChange(window.xsso.state());
        });

        obse.disconnect();
      }
    });

    observer?.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    return () => observer?.disconnect();
  }, []);

  const login = React.useCallback(() => {
    if (typeof window === "undefined") {
      return async () => void 0;
    }
    const { origin } = new URL(window.location?.href);
    const redirectUri = `${origin}/redirect/`;
    window.location.assign(`${xssoHostURL}/login?uri=${redirectUri}`);
  }, []);

  const logout = React.useCallback(() => {
    if (typeof window === "undefined") {
      return async () => void 0;
    }
    const { origin } = new URL(window.location?.href);
    const redirectUri = `${origin}/redirect/`;
    window.location.assign(`${xssoHostURL}/logout?uri=${redirectUri}`);
  }, []);

  /**
   * Hooks into the riotbar auth functions to use the library instead
   */
  const hookLoginFunctions = React.useMemo(() => {
    if (typeof window === "undefined") {
      return () => void 0;
    }
    return () => {
      window.RiotBar.account.login = login;
      window.RiotBar.account.logout = logout;
    };
  }, [login, logout]);

  // Set Auth State
  React.useEffect(() => {
    setAuthState({
      loading,
      isLoggedIn,
      login,
      logout,
      hookLoginFunctions,
      id,
    });
  }, [isLoggedIn, loading, login, id]);

  if (typeof window === "undefined") {
    return <XSSOContext.Provider value={EMPTY_AUTH_STATE}>{children}</XSSOContext.Provider>;
  }

  return <XSSOContext.Provider value={authState}>{children}</XSSOContext.Provider>;
};
